import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import classNames from 'classnames';
import { BASE_LINE_HEIGHT } from 'HubStyleTokens/sizes';
import { BASE_LINK_COLOR, BASE_LINK_HOVER_COLOR } from 'HubStyleTokens/theme';
import PropTypes from 'prop-types';
import { memo, useCallback } from 'react';
import styled, { css } from 'styled-components';
import UIClickable from '../button/UIClickable';
import SyntheticEvent from '../core/SyntheticEvent';
import Controllable from '../decorators/Controllable';
import { CARET_HEIGHT, CARET_WIDTH } from '../icon/IconConstants';
import UICaret from '../icon/icons/UICaret';
import AccordionTransition from '../transitions/AccordionTransition';
import { allSizes, toShorthandSize } from '../utils/propTypes/tshirtSize';
import { setUiTransition } from '../utils/Styles';
const indicatorCanvasStyle = {
  display: 'block',
  width: '100%',
  visibility: 'hidden'
};
const indicatorCaretStyle = {
  position: 'absolute',
  width: '100%',
  left: 0,
  top: 0
};
const headingForSize = {
  xs: 'span',
  sm: 'h5',
  md: 'h4',
  lg: 'h2',
  xl: 'h1'
};
const indicatorCoordinatesForTagName = {
  span: 'top: 7px; width: 5px;',
  h6: 'top: 4px; width: 5px;',
  h5: 'top: 3px; width: 7px;',
  h4: 'top: 4px; width: 8px;',
  h3: 'top: 4px; width: 9px;',
  h2: 'top: 3px; width: 11px;',
  h1: 'top: 10px; width: 11px;'
};
const Indicator = styled.div.withConfig({
  displayName: "UIAccordionItem__Indicator",
  componentId: "sc-1jnwj80-0"
})(["stroke:", ";position:absolute;left:0;", ";transform:", ";", ";"], BASE_LINK_COLOR, ({
  size,
  titleTagName
}) => {
  if (titleTagName) {
    return indicatorCoordinatesForTagName[titleTagName];
  } else if (size) {
    return indicatorCoordinatesForTagName[headingForSize[toShorthandSize(size)]];
  }

  return indicatorCoordinatesForTagName['span'];
}, ({
  open
}) => open && 'rotate(90deg)', setUiTransition());
const paddingLeftForTagName = {
  span: 17,
  h6: 20,
  h5: 23,
  h4: 27,
  h3: 27,
  h2: 30,
  h1: 30
};
const paddingLeftMixin = css(["padding-left:", "px;"], ({
  size,
  titleTagName
}) => {
  if (titleTagName) {
    return paddingLeftForTagName[titleTagName];
  } else if (size) {
    return paddingLeftForTagName[headingForSize[toShorthandSize(size)]];
  }

  return paddingLeftForTagName['span'];
});
const marginBottomForTagName = {
  span: 8,
  h6: 0,
  h5: 0,
  h4: 0,
  h3: 0,
  h2: 0,
  h1: 0
};
const marginBottomSpan = css(["margin-bottom:", "px;"], ({
  size,
  titleTagName
}) => {
  if (titleTagName) {
    return marginBottomForTagName[titleTagName];
  } else if (size) {
    return marginBottomForTagName[headingForSize[toShorthandSize(size)]];
  }

  return marginBottomForTagName['span'];
});
const Clickable = styled((_ref) => {
  let rest = _objectWithoutPropertiesLoose(_ref, ["open", "size", "titleTagName"]);

  return /*#__PURE__*/_jsx(UIClickable, Object.assign({}, rest));
}).withConfig({
  displayName: "UIAccordionItem__Clickable",
  componentId: "sc-1jnwj80-1"
})(["display:block;position:relative;line-height:", ";", ";", ";user-select:none;&:hover{color:", ";", "{color:currentColor;}}"], BASE_LINE_HEIGHT, paddingLeftMixin, marginBottomSpan, BASE_LINK_HOVER_COLOR, Indicator);
const Transition = styled((_ref2) => {
  let rest = _objectWithoutPropertiesLoose(_ref2, ["size", "titleTagName"]);

  return /*#__PURE__*/_jsx(AccordionTransition, Object.assign({}, rest));
}).withConfig({
  displayName: "UIAccordionItem__Transition",
  componentId: "sc-1jnwj80-2"
})(["", ";"], paddingLeftMixin);
const UIAccordionItem = /*#__PURE__*/memo(props => {
  const {
    animate = true,
    children,
    Content,
    renderContent,
    contentClassName,
    disabled = false,
    flush = false,
    onOpenChange,
    open = false,
    isCardHeader,
    size = 'xs',
    title,
    titleClassName,
    titleTagName
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["animate", "children", "Content", "renderContent", "contentClassName", "disabled", "flush", "onOpenChange", "open", "isCardHeader", "size", "title", "titleClassName", "titleTagName"]);

  const handleTitleClick = useCallback(() => {
    if (!onOpenChange) return;
    onOpenChange(SyntheticEvent(!open));
  }, [onOpenChange, open]);
  const shorthandSize = toShorthandSize(size);
  const computedTitleClassName = classNames(titleClassName, 'private-accordion');
  const Heading = titleTagName || headingForSize[shorthandSize];
  return /*#__PURE__*/_jsxs("div", Object.assign({}, rest, {
    children: [/*#__PURE__*/_jsx(Clickable, {
      "aria-expanded": open,
      className: `${computedTitleClassName} ${isCardHeader ? 'm-bottom-0' : ''}`,
      disabled: disabled,
      onClick: handleTitleClick,
      open: open,
      size: shorthandSize,
      titleTagName: titleTagName,
      children: /*#__PURE__*/_jsxs(Heading, {
        role: "presentation",
        className: flush ? 'm-bottom-0' : '',
        children: [/*#__PURE__*/_jsxs(Indicator, {
          open: open,
          size: shorthandSize,
          titleTagName: titleTagName,
          children: [/*#__PURE__*/_jsx("canvas", {
            width: CARET_WIDTH,
            height: CARET_HEIGHT,
            style: indicatorCanvasStyle
          }), /*#__PURE__*/_jsx(UICaret, {
            style: indicatorCaretStyle,
            viewBoxHeight: CARET_HEIGHT,
            viewBoxWidth: CARET_WIDTH,
            width: 12
          })]
        }), title]
      })
    }), /*#__PURE__*/_jsx(Transition, {
      Content: Content,
      renderContent: renderContent,
      duration: animate ? undefined : 0,
      className: classNames("UIAccordionItem-content private-accordion__content", contentClassName),
      open: open,
      size: shorthandSize,
      titleTagName: titleTagName,
      children: children
    })]
  }));
});
UIAccordionItem.propTypes = {
  animate: PropTypes.bool.isRequired,
  children: PropTypes.node,
  Content: PropTypes.elementType,
  renderContent: PropTypes.func,
  contentClassName: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  flush: PropTypes.bool.isRequired,
  isCardHeader: PropTypes.bool,
  onOpenChange: PropTypes.func,
  open: PropTypes.bool,
  title: PropTypes.node.isRequired,
  titleClassName: PropTypes.string,
  size: allSizes,
  titleTagName: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span'])
};
UIAccordionItem.defaultProps = {
  animate: true,
  disabled: false,
  flush: false,
  isCardHeader: false,
  open: false,
  size: 'xs'
};
UIAccordionItem.displayName = 'UIAccordionItem';
export default Controllable(UIAccordionItem, ['open']);